import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css, cx } from '@linaria/atomic';
import {
  ReactFlow,
  ReactFlowProvider,
  Panel,
  useEdgesState,
  useReactFlow,
  Background,
  applyNodeChanges,
  BackgroundVariant,
  Edge,
  Node as ReactFlowNode,
  EdgeProps,
  NodeProps,
  ProOptions,
  useStore,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { theme } from '@frontend/theme';
import { ContentLoader } from '@frontend/design-system';
import { ActionButtons } from './action-buttons';
import { EdgeType, DefaultNodeType, CustomEdgeData, DefaultNodeData } from './data';
import { AnimatedSVGEdge, AnimatedSVGEdgeReverse, BasicEdge, PhoneTreeEdge, ShortEdge } from './edges/custom';
import { BasicNode } from './nodes/basic-node';
import { PlaceholderNode } from './nodes/placeholder-node';
import { InteractionContextType, InteractionProvider } from './provider';
import { calculateLayout, compareElements, useCanvasDimensions } from './util';
import { WarningDismissalProvider } from './warning-dismissal-provider';

const flowContentDivStyle1 = css`
  transition: opacity 0.3s ease-in;
  height: 100%;
`;

const flowHidden = css`
  opacity: 0;
  pointer-events: none;
`;

const flowVisible = css`
  opacity: 1;
  pointer-events: auto;
`;

const flowContentReactFlowStyle2 = css`
  .react-flow__edges {
    /**
                Need to add width: 100%; to this class to make the edges show. This is the parent of the edges
                SVGs edges and without it, the parent div has a calculated width of 0px which causes the edges
                to not show.
               */
    width: 100%;
  }
`;

const flowContentPanelStyle3 = css`
  display: flex;
  gap: ${theme.spacing(1)};
`;

const LARGE_GRAPH_WIDTH = 1500;

const edgeTypes: Record<(typeof EdgeType)[keyof typeof EdgeType], (props: EdgeProps) => ReactNode> = {
  [EdgeType.ToTarget]: AnimatedSVGEdge,
  [EdgeType.ToTargetShort]: ShortEdge,
  [EdgeType.FromSource]: AnimatedSVGEdgeReverse,
  [EdgeType.TreeOption]: PhoneTreeEdge,
  [EdgeType.Basic]: BasicEdge,
  [EdgeType.PlaceHolder]: BasicEdge,
};

type FlowContentProps<NodeTypes extends string, DataPayload extends Record<string, any>> = {
  nodes: ReactFlowNode<DataPayload>[];
  edges: Edge<CustomEdgeData>[];
  initialNodeId?: string;
  canEdit?: boolean;
  showDebugButtons?: boolean;
  customNodeTypes?: Record<NodeTypes, (props: NodeProps<ReactFlowNode<DataPayload, NodeTypes>>) => ReactNode>;
  onPlusIconClick?: InteractionContextType['onPlusIconClick'];
};

export const FlowContent = <NodeTypes extends string, DataPayload extends Record<string, any>>({
  nodes: controlledNodes,
  edges: controlledEdges,
  showDebugButtons = false,
  canEdit = false,
  initialNodeId,
  customNodeTypes,
  onPlusIconClick,
}: FlowContentProps<NodeTypes, DataPayload>) => {
  const { setCenter, zoomIn, zoomOut, fitView, getZoom, zoomTo, getNodesBounds } = useReactFlow();
  const [nodes, setNodes] = useState<ReactFlowNode<DataPayload | DefaultNodeData>[]>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<CustomEdgeData>>([]);

  // Custom onNodesChange to pass as callback to <ReactFlow> component that creates new node objects when there are
  // dimension changes. This prevents applyNodeChanges from mutating objects that zustand's previous snapshot still
  // references. applyNodeChanges mutates the same objects in the nodes array (mutates object by reference), which
  // causes issues with our custom compare function (compareElements -> nodesEqualityFn) that checks for dimension
  // changes since it will see the old values as equal to the new values and skip triggering layout recalculations.
  // NOTE: onNodesChange is called for every type of node change — selections, position updates during drag, removes,
  // additions, etc
  const onNodesChange = useCallback(
    (changes: Parameters<typeof applyNodeChanges<ReactFlowNode<DataPayload | DefaultNodeData>>>[0]) => {
      setNodes((currentNodes) => {
        const hasDimensionChanges = changes.some((c) => c.type === 'dimensions');
        // Only create new node objects when there are dimension changes since those are the only changes that can cause
        // issues with our custom compare function. For other changes, we can just pass the current nodes to applyNodeChanges
        // to avoid cloning all nodes on every call which would create N new objects for no benefit.
        if (hasDimensionChanges) {
          const clonedNodes = currentNodes.map((n) => ({
            ...n,
            measured: n.measured ? { ...n.measured } : undefined,
          }));
          return applyNodeChanges(changes, clonedNodes);
        }
        return applyNodeChanges(changes, currentNodes);
      });
    },
    []
  );
  const [mode, setMode] = useState<'edit' | 'view'>(canEdit ? 'edit' : 'view');
  const [graphDirection, setGraphDirection] = useState<'TB' | 'LR'>('TB');
  const [initialEditModeCenteringDone, setInitialEditModeCenteringDone] = useState(false);

  const memoizedNodeTypes = useMemo(
    () => ({
      [DefaultNodeType.Basic]: BasicNode,
      [DefaultNodeType.PlaceHolder]: PlaceholderNode,
      ...(customNodeTypes ? customNodeTypes : {}),
    }),
    [customNodeTypes]
  );

  const graphElements = useStore(
    (state) => ({
      nodes: state.nodes,
      edges: state.edges,
    }),
    // The compare elements function will only update `graphElements` if something has
    // changed that should trigger a layout. This includes changes to a node's
    // dimensions, the number of nodes, or changes to edge sources/targets.
    compareElements
  );

  const { width: viewportWidth, height: viewportHeight } = useCanvasDimensions();

  // Track whether we should animate to the target node (used for smooth transitions) instead of
  // re-centering the graph in the next reset.
  const shouldAnimateToNodeRef = useRef<string | undefined>(undefined);

  const prevInitialNodeId = useRef<string | undefined>(initialNodeId);
  useEffect(() => {
    // If the initial node ID changes, we may need to re-center the graph
    if (initialNodeId && prevInitialNodeId.current !== initialNodeId) {
      if (initialEditModeCenteringDone) {
        // If centering was already done, don't reset and show loader.
        // Instead, mark that we should animate to this node after layout recalculation.
        shouldAnimateToNodeRef.current = initialNodeId;
      } else {
        // Initial centering hasn't been done yet, so we need to wait for measurement
        // Reset initial centering to re-center on the new initial node ID if it changes
        setInitialEditModeCenteringDone(false);
      }
    }
    prevInitialNodeId.current = initialNodeId;
  }, [initialNodeId, initialEditModeCenteringDone]);

  // This useEffect handles syncing the reactFlow nodes and edges state with any updates coming from the
  // controlledNodes and controlledEdges props (controlled parent state), as well as adding placeholder nodes/edges
  // when in edit mode.
  // NOTE: we always need to feed the nodes/edges to react flow before we can feed them to dagrejs (or whichever
  // layout engine we use) so that react flow can render them and get their dimensions (width and height) which
  // it adds to the nodes "measured" property. Once the nodes have their dimensions, we can then calculate the layout
  // to get their correct positions in the graph.
  useEffect(() => {
    // Takes controlled props (nodes/edges from parent) and reconciles them with the internal React Flow state
    // while preserving measurements and positions.
    const hydrateElements = (
      nodesToHydrate: ReactFlowNode<DataPayload | DefaultNodeData>[],
      edgesToHydrate: Edge<CustomEdgeData>[]
    ) => {
      if (nodesToHydrate.length === 0 || edgesToHydrate.length === 0) {
        return;
      }

      const updatedNodes = nodesToHydrate.map((node) => {
        const existingNode = nodes.find((n) => n.id === node.id);
        if (!existingNode) {
          // If controlled nodes/edges do not currently exist in react flow's nodes/edges state then set their
          // visibility to hidden since they are new nodes to display in the canvas. This is to prevent briefly
          // displaying nodes in a wrong position before their width/height have been measured and their layout
          // calculated to get their correct position.
          return { ...node, hidden: true };
        }

        // If the node already exists in the react flow state, then copy over their current measure width/height
        // also their position so that we do not lose their current layout. We need to do this because of the
        // controlled nature of the nodes state. The controlled nodes state from the props does not include
        // position or measured dimensions.
        return {
          ...node,
          position: existingNode.position,
          measured: existingNode.measured,
          data: { ...existingNode.data, ...node.data },
        };
      });

      const updatedEdges = edgesToHydrate.map((edge) => {
        const existingEdge = edges.find((e) => e.id === edge.id);
        if (!existingEdge) {
          // If this is a new edge then set its visibility to hidden since it is a new edge to display in the canvas.
          // This is to prevent briefly displaying edges in a wrong position before the layout of their target/source
          // nodes have been calculated.
          return { ...edge, hidden: true };
        }
        return edge;
      });

      // Update the react flow nodes and edges state with the updated nodes and edges
      setNodes(updatedNodes);
      setEdges(updatedEdges);
    };

    if (mode === 'edit') {
      // Add placeholder nodes and edges when in edit mode. These are the "plus" icons that allow adding new nodes.
      const placeholders = getPlaceHolders(controlledNodes, controlledEdges);
      const combinedNodes = [...controlledNodes, ...placeholders.nodes];
      const combinedEdges = [...controlledEdges, ...placeholders.edges];

      hydrateElements(combinedNodes, combinedEdges);
    } else if (mode === 'view') {
      hydrateElements(controlledNodes, controlledEdges);
    }
  }, [controlledNodes, controlledEdges, mode]);

  // Calculate the layout of the current nodes and edges in the react flow state to get
  // their correct layout positions (x and y coordinates) in the graph and then update
  // the react flow nodes/edges state with the calculated nodes/edges.
  const recalculateLayout = async () => {
    const { nodes: calculatedNodes, edges: calculatedEdges } = await calculateLayout(
      nodes as ReactFlowNode<DefaultNodeData>[],
      edges,
      {
        direction: graphDirection,
        ranksep: mode === 'edit' ? 75 : 50,
      }
    );

    // Update the react flow nodes and edges state with the calculated nodes and edges
    setNodes(calculatedNodes);
    setEdges(calculatedEdges);

    return { nodes: calculatedNodes, edges: calculatedEdges };
  };

  // Whenever the react flow state elements (nodes/edges) change, recalculate the layout
  useEffect(() => {
    if (graphElements.nodes.length === 0) {
      return;
    }

    const recalculate = async () => {
      const { nodes: calculatedNodes, edges: calculatedEdges } = await recalculateLayout();

      if (!initialEditModeCenteringDone) {
        handleInitialCentering(calculatedNodes, calculatedEdges);
      } else if (shouldAnimateToNodeRef.current) {
        // Smoothly animate to the target node without resetting centering state
        const targetNodeId = shouldAnimateToNodeRef.current;
        shouldAnimateToNodeRef.current = undefined;

        centerOnNode(calculatedNodes, targetNodeId);
      }
    };

    // Recalculate the layout whenever react flow state elements (nodes/edges) change.
    recalculate();
  }, [graphElements, graphDirection]);

  /**
   * Centers the viewport on a specific node within the graph.
   * Adjusts zoom level based on graph size characteristics.
   *
   * @param calculatedNodes - The nodes with calculated layout positions
   * @param targetNodeId - Optional ID of the node to center on. If not provided, centers on first node.
   * @param options - Optional settings for centering behavior
   */
  const centerOnNode = (
    calculatedNodes: ReactFlowNode<DataPayload | DefaultNodeData>[],
    targetNodeId?: string,
    options?: { adjustZoom?: boolean }
  ) => {
    const { adjustZoom = false } = options ?? {};

    // This provides the width and height of a set of nodes. It is the graph width/height if you
    // provide it all the calculated nodes in the graph.
    const nodeBounds = getNodesBounds(calculatedNodes);

    // Determine graph size characteristics
    const isLargeGraph = nodeBounds.width > LARGE_GRAPH_WIDTH;
    // Tiny graph is determined by being less than half the viewport width
    const isTinyGraph = nodeBounds.width < viewportWidth * 0.5;

    // Find the node to center on
    const targetNode = targetNodeId
      ? calculatedNodes.find((node) => node.id === targetNodeId) ?? calculatedNodes[0]
      : calculatedNodes[0];

    if (!targetNode) {
      return;
    }

    const nodeX = targetNode.position.x;
    const nodeY = targetNode.position.y;

    if (adjustZoom) {
      // Adjust zoom based on graph size (used for initial centering)
      if (isLargeGraph) {
        // For large graphs, zoom in on the start node to show it plus content below
        // Position the start node at the top to keep it high
        const yOffset = viewportHeight * 0.5;
        setCenter(nodeX, nodeY + yOffset, { zoom: 0.8, duration: 800 });
      } else if (isTinyGraph) {
        // For tiny graphs, center on the start node and zoom out to show more vertical space
        // Position start node at the top to maximize visible space below
        const yOffset = viewportHeight * 0.4;
        setCenter(nodeX, nodeY + yOffset, { zoom: 1, duration: 800 });
      } else {
        // For medium graphs, keep the default fitView zoom but reposition to show start node high
        const currentZoom = getZoom();
        // Do not zoom in more than 1x since it can cause the graph to place the top start node past the top of the
        // viewport when we add the yOffset
        const minZoom = Math.min(currentZoom, 1);
        const yOffset = viewportHeight * 0.4;
        setCenter(nodeX, nodeY + yOffset, { zoom: minZoom });
      }
    } else {
      // Just center without adjusting zoom (used for smooth transitions)
      const yOffset = viewportHeight * 0.4;
      setCenter(nodeX, nodeY + yOffset, { duration: 800 });
    }
  };

  const handleInitialCentering = (
    calculatedNodes: ReactFlowNode<DataPayload | DefaultNodeData>[],
    _calculatedEdges: Edge<CustomEdgeData>[]
  ) => {
    // Maximum number of retry attempts to prevent infinite loops
    // At ~60fps, 120 attempts = ~2 seconds of waiting for measurement
    const MAX_ATTEMPTS = 120;
    let attempts = 0;

    const attemptCentering = () => {
      window.requestAnimationFrame(() => {
        attempts++;

        const allNodesMeasured = nodes.every((node) => {
          return node.measured && (node.measured?.width ?? 0) > 0 && (node.measured?.height ?? 0) > 0;
        });

        if (!allNodesMeasured) {
          if (attempts < MAX_ATTEMPTS) {
            // Retry on next animation frame if not all nodes have been measured yet
            attemptCentering();
          } else {
            // Give up after max attempts and show the graph anyway to avoid infinite loading
            console.warn('handleInitialCentering: Max attempts reached, showing graph without centering');
            setInitialEditModeCenteringDone(true);
          }
          return;
        }

        // Allow time for fitView to complete before applying custom centering
        setTimeout(() => {
          centerOnNode(calculatedNodes, initialNodeId, { adjustZoom: true });
        }, 100); // Small delay to ensure fitView has been applied

        // Mark initial centering as done to show the flow content (remove loading state)
        setInitialEditModeCenteringDone(true);
      });
    };

    // Start the centering attempt
    attemptCentering();
  };

  const handleZoom = useCallback((direction: 'in' | 'out') => {
    const currentZoom = getZoom();

    if (direction === 'in') {
      zoomTo(currentZoom + 0.3, { duration: 300 });
    } else {
      zoomTo(currentZoom - 0.3, { duration: 300 });
    }
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === '=') {
        handleZoom('in');
      } else if (event.key === '-') {
        handleZoom('out');
      } else if (event.key === '/') {
        fitView({ duration: 300 });
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [zoomIn, zoomOut]);

  /**
   * Helper function to get all the placeholder nodes and edges that can be added on to a
   * given graph. This is used to add the placeholder nodes (plus icons) and edges when in edit mode.
   *
   * @param graphNodes The nodes in the graph
   * @param graphEdges The edges in the graph
   * @returns
   */
  const getPlaceHolders = (graphNodes: ReactFlowNode<DataPayload>[], graphEdges: Edge<CustomEdgeData>[]) => {
    const placeHolderNodes: ReactFlowNode<DefaultNodeData>[] = [];
    const placeHolderEdges: Edge<CustomEdgeData>[] = [];
    // Get all the leaf nodes (have no children) that can be added on to
    const extendableLeafNodes = graphNodes.filter((node) => {
      const hasBlockingChildEdge = graphEdges.some(
        (edge) => edge.source === node.id && !edge.data?.useManualLayout && edge.type !== EdgeType.PlaceHolder
      );
      return !hasBlockingChildEdge && node.data?.isExtendable;
    });

    // For each extendable leaf node, add a placeholder edge and placeholder node to it
    extendableLeafNodes.forEach((node) => {
      const placeHolderNodeId = `${node.id}-placeholder`;
      const hasPlaceholderWarning = !!node.data?.placeholderWarning;
      const placeholderNode: ReactFlowNode<DefaultNodeData> = {
        id: placeHolderNodeId,
        type: DefaultNodeType.PlaceHolder,
        position: { x: node.position.x, y: node.position.y },
        data: { id: placeHolderNodeId, label: '', parentId: node.id, warning: node.data?.placeholderWarning },
        // React Flow renders each node in its own stacking context. Added zIndex: 1 to placeholder nodes that have a
        // warning — this tells React Flow to elevate that node's wrapper div above other nodes (which default to
        // zIndex: 0), so the warning tooltip renders on top of neighboring nodes' content.
        ...(hasPlaceholderWarning && { zIndex: 1 }),
      };

      const placeholderEdge = {
        id: `${node.id}-placeholder-edge`,
        source: node.id,
        target: placeholderNode.id,
        type: EdgeType.PlaceHolder,
        selectable: false,
        data: {},
      };

      placeHolderNodes.push(placeholderNode);
      placeHolderEdges.push(placeholderEdge);
    });

    return { nodes: placeHolderNodes, edges: placeHolderEdges };
  };

  const handleNodeExpansion = async () => {
    return await recalculateLayout();
  };

  const handleLayoutChange = async (direction: 'TB' | 'LR') => {
    setGraphDirection(direction);
    return await recalculateLayout();
  };

  const proOptions: ProOptions = { account: 'paid-pro', hideAttribution: true };

  return (
    <WarningDismissalProvider>
      <InteractionProvider mode={mode} onPlusIconClick={onPlusIconClick} onNodeExpansion={handleNodeExpansion}>
        <ContentLoader show={!initialEditModeCenteringDone} />
        <div className={cx(flowContentDivStyle1, initialEditModeCenteringDone ? flowVisible : flowHidden)}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            edgeTypes={edgeTypes}
            nodeTypes={memoizedNodeTypes}
            nodesDraggable={false}
            panOnScrollSpeed={1}
            minZoom={0.1} // Allows zooming out really far
            deleteKeyCode={null}
            proOptions={proOptions}
            panOnScroll
            fitView
            className={flowContentReactFlowStyle2}
          >
            {/* Control Panel that has the zoom-in, zoom-out and fit view buttons */}
            <Panel position='top-left' className={flowContentPanelStyle3}>
              <ActionButtons
                mode={mode}
                showDebugButtons={showDebugButtons}
                onLayoutChange={handleLayoutChange}
                setMode={setMode}
                onZoomClick={handleZoom}
                onFitViewClick={() => {
                  fitView({ duration: 300 });
                }}
              />
            </Panel>

            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        </div>
      </InteractionProvider>
    </WarningDismissalProvider>
  );
};

export function Flow() {
  return (
    <div style={{ width: '100%', height: '100%' }}>
      <ReactFlowProvider>
        <FlowContent
          nodes={[
            {
              id: 'playground',
              type: 'playground',
              position: { x: -100, y: -100 },
              data: { id: 'playground', label: 'Playground', warning: '' },
            },
          ]}
          edges={[]}
        />
      </ReactFlowProvider>
    </div>
  );
}
